import React from 'react';
import ReactDOM from 'react-dom';

import camelize from 'camelize';
import snakeize from 'snakeize';

import smoothscroll from 'smoothscroll-polyfill';

import { CookiesProvider } from 'react-cookie';

import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'react-router-redux';

import {
  fetchFirebaseToken,
  fetchMyInfo,
  setAdLoadStatus,
  setExperiments,
  setValue,
  showStorageWarning,
} from 'app/redux/common/actions';
import createStore from 'app/redux/store';
import { createBrowserHistory as createHistory } from 'history';

import { AdManager } from 'app/utils/ad';
import apiClient from 'app/utils/apiClient';
import {
  AD_STATUSES,
  AUTH_STATUSES,
  REMOTE_CONFIG_KEYS,
} from 'app/utils/constants';
import { formatDate } from 'app/utils/datetime';
import { checkIosVersion } from 'app/utils/device';
import { ScrollUtil } from 'app/utils/dom';
import Interface from 'app/utils/interface';
import LocalForage from 'app/utils/localForage';
import LocalStorage from 'app/utils/localStorage';
import { logScreen } from 'app/utils/screen';
import { getParams } from 'app/utils/url';
import { isNull, isNumeric } from 'app/utils/validators';

import { loadComponents } from 'loadable-components';

import 'firebase/analytics';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';

import VConsole from 'vconsole';

import DevTools from 'app/components/DevTools';
import AudioRecorder from 'audio-recorder-polyfill'; // eslint-disable-line

import * as Sentry from '@sentry/browser';

const { GTM_ID, SENTRY_DSN, RELEASE, VERSION, NODE_ENV, APP_ENV } = process.env;

const __PROD__ = APP_ENV === 'production';

smoothscroll.polyfill();

window.MediaRecorder = AudioRecorder;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: APP_ENV || 'staging',
    release: RELEASE,
    integrations: [
      new Sentry.Integrations.Breadcrumbs({
        console: false,
      }),
    ],
    beforeBreadcrumb: (data) => {
      const { breadcrumbs } = data;
      if (!Array.isArray(breadcrumbs)) {
        return true;
      }
      if (breadcrumbs.length <= 0) {
        return true;
      }
      const lastBreadcrumb = breadcrumbs.slice(-1)[0];
      const { category, data: request } = lastBreadcrumb;
      if (category !== 'fetch' || isNull(request)) {
        return true;
      }
      const statusCode = request.status_code;
      if (!isNumeric(statusCode)) {
        return true;
      }
      if (statusCode < 500) {
        return false;
      }
      return true;
    },
    ignoreErrors: [
      'RequestError',
      'AbortError',
      'window.AnalyticsWebInterface',
      'Java exception was raised during method invocation',
      'Non-Error promise rejection',
      'Maximum call stack size exceeded',
      'Loading chunk',
      'NotAllowedError: play() can only be initiated by a user gesture.',
      'Unexpected identifier',
      '404 - {"result":{"code":"NotFound","message":"Not found.","errors":{}},"status_code":404}',
      '401 - {"result":{"code":"NotAuthenticated","message":"Authentication credentials were not provided.","errors":{}},"status_code":401}',
      'QuotaExceededError',
      'UnknownError: Internal error opening backing store for indexedDB.open.',
      'UnknownError: Connection to Indexed Database server lost.',
      'UnknownError: An internal error was encountered in the Indexed Database server',
      'Non-Error exception',
      'NotSupportedError',
      'Network Error',
      'Invalid attempt to spread non-iterable instance.',
      'URIError',
      'A network error (such as timeout, interrupted connection or unreachable host) has occurred.',
      'undefined is not iterable (cannot read property Symbol(Symbol.iterator))',
      'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.',
      "Cannot read properties of undefined (reading 'code')",
      "Cannot read property 'postMessage' of null",
      'TypeError: Failed to fetch',
    ],
  });
  Sentry.setTag('version', VERSION);
}

// localforage setup
LocalForage.create();

const history = createHistory();
const store = createStore(history, window.__INITIAL_STATE__);
const state = store.getState();
const {
  user: { data: user },
  platform,
  version,
} = state.common;

if (!__PROD__) {
  window.localForage = LocalForage;
  window.store = store;

  if (platform) {
    const vConsole = new VConsole();
    vConsole.setOption({ maxLogNumber: 5000 });
  }
}

// initialize tagManager web platform
const initGTM = (_userId = null) => {
  const userId = _userId || user.id;
  const tagManagerArgs = {
    gtmId: GTM_ID,
    dataLayer: {},
  };
  if (userId) {
    tagManagerArgs['dataLayer']['user_id'] = userId;
  }
  TagManager.initialize(tagManagerArgs);
};
window.tagManagerTriggerEvent = () => {};
window.tagManagerPushVariables = () => {};
if (!platform) {
  initGTM();
  window.tagManagerPushVariables = (variables) => {
    TagManager.dataLayer({
      dataLayer: snakeize(variables),
    });
  };
  window.tagManagerTriggerEvent = (key, params) => {
    const { source } = store.getState().common;
    const reducedParams = Object.keys(params).reduce(
      (_params, key) => {
        const value = params[key];

        // if (typeof value === 'number') {
        //   return {
        //     ..._params,
        //     [key]: value.toString(),
        //   };
        // }

        if (value === null) {
          return {
            ..._params,
            [key]: undefined,
          };
        }

        return {
          ..._params,
          [key]: value,
        };
      },
      source ? { source } : {}
    );

    if (!__PROD__) {
      console.log('GTM:', key, reducedParams);
    }
    TagManager.dataLayer({
      dataLayer: {
        event: key,
        ...snakeize(reducedParams),
      },
    });
  };
}

window.__chatie_loaded_images__ = {};

// Ad manager
window.Ad = new AdManager(store);

// firebase
const {
  FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID,
  FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID,
} = process.env;

const firebaseApp =
  firebase.apps.length === 0
    ? firebase.initializeApp({
        apiKey: FIREBASE_API_KEY,
        authDomain: FIREBASE_AUTH_DOMAIN,
        projectId: FIREBASE_PROJECT_ID,
        appId: FIREBASE_APP_ID,
        measurementId: FIREBASE_MEASUREMENT_ID,
      })
    : firebase.app();

// define native interface
window.chatieInterface = new Interface(store, firebaseApp);
window.ci = window.chatieInterface;

// scroll back to top on location change
window.prevLocation = {};
window.currentLocation = history.location;

logScreen(history.location.pathname, history.location.search);

const setMaintenance = (isMaintenance, maintenanceExtraText) => {
  store.dispatch(setValue('isMaintenance', isMaintenance));
  store.dispatch(setValue('maintenanceExtraText', maintenanceExtraText));

  if (platform) {
    window.ci.web.startMaintenance();
  } else {
    history.replace('/maintenance');
  }
};

// check maintenance
const { MAINTENANCE_URL } = process.env;
let isMaintenance = false;
let maintenanceExtraText = false;
// let isClientVersionUpdated = false;

if (MAINTENANCE_URL) {
  const checkMaintenance = () => {
    apiClient
      .get(MAINTENANCE_URL, { asPromise: true })
      .then(({ result }) => {
        isMaintenance = result.isMaintenance; // eslint-disable-line
        maintenanceExtraText = result.text;
        if (isMaintenance) {
          setMaintenance(isMaintenance, maintenanceExtraText);
        }
      })
      .catch(() => {});
  };
  const tenMinute = 10 * 60 * 1000;

  setInterval(() => {
    checkMaintenance();
  }, tenMinute);

  checkMaintenance();
}

history.listen((location, action) => {
  if (Object.keys(window.__chatie_loaded_images__).length > 1000) {
    window.__chatie_loaded_images__ = {};
  }

  if (location.pathname === '/maintenance') {
    return;
  }

  if (isMaintenance) {
    setMaintenance(isMaintenance, maintenanceExtraText);
    return;
  }

  const { hash, search, pathname } = location;
  const {
    hash: prevHash,
    search: prevSearch,
    pathname: prevPathname,
  } = window.currentLocation;

  const prevParams = getParams(prevSearch || '');
  const params = getParams(search);

  const pathChanged = prevPathname !== pathname;
  const hashChanged = prevHash !== hash;
  const menuChanged = prevParams.menu !== params.menu;
  const pageChanged = prevParams.page !== params.page;

  if (pathChanged || action !== 'REPLACE') {
    window.prevLocation = window.currentLocation;
  }

  window.currentLocation = location;

  if (pathChanged || menuChanged) {
    logScreen(location.pathname, location.search);
  }

  if (pathChanged || hashChanged || pageChanged) {
    window.scrollTo(0, 0);
  }
});

ScrollUtil.init();

const rootEl = document.getElementById('root');

if (!platform && __PROD__) {
  document.oncontextmenu = () => false;
}

const render = (App) => {
  const container = (
    <CookiesProvider>
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <App />
        </ConnectedRouter>
      </Provider>
    </CookiesProvider>
  );

  if (NODE_ENV === 'production' && rootEl.hasChildNodes()) {
    ReactDOM.hydrate(container, rootEl);
  } else {
    ReactDOM.render(container, rootEl);
  }
};

// sentry setup
Sentry.setUser(user);

// firebase auth
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const retry = (func, delay, retries) =>
  new Promise((resolve, reject) => {
    return func()
      .then(resolve)
      .catch((reason) => {
        if (retries > 0) {
          return wait(delay)
            .then(retry.bind(null, func, delay, retries - 1))
            .then(resolve)
            .catch(reject);
        }
        return reject(reason);
      });
  });

const signInWithCustomToken = () =>
  store
    .dispatch(fetchFirebaseToken())
    .then(({ value }) => {
      const { token } = value.result;

      const authenticate = () =>
        new Promise((resolve, reject) =>
          firebase
            .auth()
            .signInWithCustomToken(token)
            .then(resolve)
            .catch(reject)
        );

      return retry(authenticate, 3000, 4);
    })
    .catch((error) => {
      Sentry.withScope((scope) => {
        scope.setExtra('retry', true);
        Sentry.captureException(error);
      });
    });

if (platform) {
  firebase.auth().setPersistence(firebase.auth.Auth.Persistence.NONE);
}

firebase.auth().onIdTokenChanged(async (firebaseUser) => {
  if (firebaseUser) {
    const idToken = await firebaseUser.getIdToken();
    store.dispatch(setValue('firebaseIdToken', idToken));
    LocalStorage.setItem('firebaseIdToken', idToken);
    const expires = new Date(Date.now() + 1000 * 60 * 60);
    document.cookie = `firebaseIdToken=${encodeURIComponent(
      idToken
    )}; expires=${expires}; domain=chatie.me; path=/;`;
  } else {
    store.dispatch(setValue('authStatus', AUTH_STATUSES.FAILED));
  }
});

firebase.auth().onAuthStateChanged(async (firebaseUser) => {
  if (firebaseUser) {
    const idToken = await firebaseUser.getIdToken();
    store.dispatch(fetchMyInfo(idToken, true));

    if (!platform) {
      initGTM(parseInt(firebaseUser.uid, 10));
    }
    LocalStorage.setItem('firebaseIdToken', idToken);
    const expires = new Date(Date.now() + 1000 * 60 * 60);
    document.cookie = `firebaseIdToken=${encodeURIComponent(
      idToken
    )}; expires=${expires}; domain=chatie.me; path=/;`;
  } else {
    if (!platform) {
      initGTM();
    }
    store.dispatch(setValue('authStatus', AUTH_STATUSES.FAILED));
  }
});

const fireStore = firebaseApp.firestore();
window.fireStore = fireStore;

// check app
const { userAgent } = navigator;
const iosVersion = checkIosVersion(userAgent);
if (userAgent.toLowerCase().indexOf('whale') !== -1) {
  alert(
    '네이버 웨일은 블룸 사용에 불편함이 있을 수 있습니다.\n원활한 서비스 이용을 위해 다른 브라우저를 이용해주세요.'
  );
}

// check revisit
const checkRevisit = () => {
  const revisitData = LocalStorage.getItem('revisit', {
    date: 0,
    count: 0,
    firstDate: 0,
  });
  const revisitCount = revisitData.count + 1;
  if (revisitCount > 7) {
    return;
  }

  const now = new Date();
  const nowToInt = parseInt(formatDate(now, 'yyyyMMdd'), 10);

  if (revisitData.date >= nowToInt) {
    return;
  }

  if (revisitCount === 3) {
    window.tagManagerTriggerEvent('revisit_3', {
      firstDate: revisitData.firstDate,
      lastDate: now.toISOString(),
    });
  } else if (revisitCount === 7) {
    window.tagManagerTriggerEvent('revisit_7', {
      firstDate: revisitData.firstDate,
      lastDate: now.toISOString(),
    });
  }

  LocalStorage.setItem('revisit', {
    date: nowToInt,
    count: revisitCount,
    firstDate: revisitData.firstDate === 0 ? now : revisitData.firstDate,
  });
};

// in app setup
if (platform) {
  const html = document.getElementsByTagName('html')[0];
  html.className += ` ${platform}`;

  if (iosVersion) {
    html.className += ` ${platform}--${iosVersion}`;
  }
}

const renderApp = async () => {
  await LocalForage.convertLegacyAsync();

  LocalForage.check().catch((err) => {
    if (!err || err.name === 'QuotaExceededError') {
      store.dispatch(showStorageWarning());
    }
  });

  const App = require('../app/containers/AppContainer').default;

  const AppWithDevTools = __DEVTOOLS__
    ? (props) => (
        <React.Fragment>
          <App {...props} />
          <DevTools />
        </React.Fragment>
      )
    : App;

  loadComponents()
    .then(() => {
      if (user.id) {
        return signInWithCustomToken();
      }

      return Promise.resolve();
    })
    .then(() => {
      render(AppWithDevTools);
    });
};

if (user.id) {
  window.ci.firebase.setUserProperty('chatie_uid', user.id);
  checkRevisit();
}

// in app events
window.ci.on('setSessionId', (e) => {
  const { sessionId } = e.detail;
  store.dispatch(setValue('sessionId', sessionId));
});

window.ci.on('transferABTestData', (e) => {
  const { data: _data } = e.detail;
  if (typeof _data !== 'object') {
    return;
  }

  const camelizedData = camelize(_data);
  const convertValue = (value) => {
    if (value === 'true') {
      return true;
    } else if (value === 'false') {
      return false;
    } else if (/^-?\d+$/.test(value)) {
      return parseInt(value, 10);
    }

    return value;
  };

  const data = Object.keys(camelizedData).reduce(
    (acc, key) => ({
      ...acc,
      [key]: convertValue(camelizedData[key]),
    }),
    {}
  );

  store.dispatch(setExperiments(data));
});

window.ci.web.transferRemoteConfigValue(REMOTE_CONFIG_KEYS.SHOW_AD_TAP_COUNT);
window.ci.web.transferRemoteConfigValue(REMOTE_CONFIG_KEYS.EPISODE_AD_ORDER);
window.ci.web.transferRemoteConfigValue(
  REMOTE_CONFIG_KEYS.INDUCE_OFFERWALL_TYPE
);
// window.ci.web.transferRemoteConfigValue(REMOTE_CONFIG_KEYS.BULK_PURCHASE_COUNT);
// window.ci.web.transferRemoteConfigValue(REMOTE_CONFIG_KEYS.CERT_POPUP_DURATION);
window.ci.on('transferRemoteConfigValue', (e) => {
  const { key, value } = e.detail;
  if (!value) {
    return;
  }
  switch (key) {
    case REMOTE_CONFIG_KEYS.SHOW_AD_TAP_COUNT:
      store.dispatch(setValue('showAdTapCount', parseInt(value, 10)));
      break;
    case REMOTE_CONFIG_KEYS.EPISODE_AD_ORDER:
      store.dispatch(setValue('episodeAdOrder', value));
      break;
    case REMOTE_CONFIG_KEYS.INDUCE_OFFERWALL_TYPE:
      store.dispatch(setValue('induceOfferwallType', value));
      break;
    // case REMOTE_CONFIG_KEYS.BULK_PURCHASE_COUNT:
    //   store.dispatch(setValue('bulkPurchaseCount', parseInt(value, 10)));

    // case REMOTE_CONFIG_KEYS.CERT_POPUP_DURATION:
    //   store.dispatch(setValue('certPopupDuration', value));
    //   break;
    default:
      break;
  }
});

// for adMob
window.ci.on('loadedAd', (e) => {
  const { adType } = e.detail;
  store.dispatch(setAdLoadStatus(adType, AD_STATUSES.SUCCESS));
});

window.ci.on('openedAd', () => {});

// window.ci.on('rewardedAd', () => {});

window.ci.on('closedAd', (e) => {
  const { autoLoad } = store.getState().common.currentAdMeta;
  const { adType, unitId } = e.detail;
  store.dispatch(setAdLoadStatus(adType, AD_STATUSES.LOADING));
  if (autoLoad === false) {
    return;
  }
  window.ci.web.loadAd(unitId);
});

window.ci.on('failedToLoadAd', (e) => {
  const { adType } = e.detail;
  store.dispatch(setAdLoadStatus(adType, AD_STATUSES.FAILED));
});

window.ci.on('data', (e) => {
  const { key, value } = e.detail;

  switch (key) {
    case 'firebaseIdToken':
      document.cookie = `firebaseIdToken=${encodeURIComponent(
        value.idToken
      )}; domain=chatie.me; path=/;`;
      store.dispatch(setValue('firebaseIdToken', value.idToken));
      break;
    default:
      break;
  }
});

window.ci.web.initialized();

// render app
renderApp();

/* eslint-disable */
if (
  NODE_ENV === 'production' &&
  window.__REACT_DEVTOOLS_GLOBAL_HOOK__ &&
  Object.keys(window.__REACT_DEVTOOLS_GLOBAL_HOOK__._renderers).length
)
  window.__REACT_DEVTOOLS_GLOBAL_HOOK__._renderers = {};
/* eslint-enable */

// hot module reloading
if (module.hot) {
  // const reRenderApp = () => {
  //   try {
  //     renderApp();
  //   } catch (error) {
  //     const RedBox = require('redbox-react');
  //     ReactDOM.render(<RedBox error={error} />, rootEl);
  //   }
  // };

  module.hot.accept();
  // module.hot.accept('../app/containers/AppContainer', () => {
  //   setImmediate(() => {
  //     // Preventing the hot reloading error from react-router
  //     ReactDOM.unmountComponentAtNode(rootEl);
  //     reRenderApp();
  //   });
  // });
}



// WEBPACK FOOTER //
// ./src/client/index.jsx